import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../../lib/api';
import { ApiError } from '../../lib/api';
import { questsApi } from '../quests/api';
import type { Quest } from '../quests/types';

export function QuestsListPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const teams = useQuery({ queryKey: ['teams'], queryFn: () => api<{ teams: { id: string; name: string }[] }>('/api/teams') });
  const [filterTeam, setFilterTeam] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  // "Show inactive" reveals cancelled + archived + completed quests
  // in the default "all" view. When the user picks a specific status
  // filter, this toggle is disabled (the filter takes priority).
  const [showInactive, setShowInactive] = useState(false);
  const activeOnly = !showInactive && !filterStatus;
  const quests = useQuery({
    queryKey: ['admin-quests', filterTeam, filterStatus, showInactive],
    queryFn: () => questsApi.list({
      team_id: filterTeam || undefined,
      // When "show inactive" is on and no specific status is selected,
      // leave status undefined to show all (open, paused, completed,
      // cancelled, archived). Otherwise honour the status filter.
      status: filterStatus
        ? (filterStatus as Quest['status'])
        : (activeOnly ? 'open' : undefined),
    }),
    // Always refetch on mount; the cached list can be stale by the
    // time the user navigates back from create/edit. The refetch is
    // fast (10ms) and the new list state renders once it lands.
    refetchOnMount: 'always',
    staleTime: 0,
  });
  const cancel = useMutation({
    mutationFn: (id: string) => questsApi.cancel(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-quests'] }),
  });
  const archive = useMutation({
    mutationFn: (id: string) => questsApi.archive(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-quests'] }),
  });
  const restore = useMutation({
    mutationFn: (id: string) => questsApi.restore(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-quests'] }),
  });

  const teamName = (id: string) => (teams.data?.teams ?? []).find((t) => t.id === id)?.name ?? id;

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Quests</h1>
          <p className="text-ink-muted">
            Assign work to specific players or open it for anyone to grab. Quests are admin-curated, player-driven.
          </p>
        </div>
        <button className="btn-primary" onClick={() => navigate('/admin/quests/new')}>+ New quest</button>
      </header>

      <div className="card flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <label className="label" htmlFor="qteam">Team</label>
          <select id="qteam" className="input" value={filterTeam} onChange={(e) => setFilterTeam(e.target.value)}>
            <option value="">All teams</option>
            {(teams.data?.teams ?? []).map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <label className="label" htmlFor="qstatus">Status</label>
          <select id="qstatus" className="input" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="">All</option>
            <option value="open">Open</option>
            <option value="paused">Paused</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
            <option value="archived">Archived</option>
          </select>
        </div>
        <label className="ml-auto flex cursor-pointer items-center gap-2 text-sm text-ink-muted">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            disabled={!!filterStatus}
          />
          <span>Show inactive (cancelled, archived, completed)</span>
        </label>
      </div>

      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-ink-muted">
            <tr><th className="py-2">Title</th><th>Team</th><th>Mode</th><th>XP</th><th>Claimed</th><th>Status</th><th>Due</th><th></th></tr>
          </thead>
          <tbody>
            {quests.isLoading ? (
              <tr><td colSpan={8} className="py-4 text-center text-ink-muted">Loading…</td></tr>
            ) : ((quests.data?.quests ?? []).length ?? 0) === 0 ? (
              <tr><td colSpan={8} className="py-4 text-center text-ink-muted">No quests match the filter.</td></tr>
            ) : (
              (quests.data?.quests ?? []).map((q) => (
                <tr key={q.id} className="border-t border-white/5">
                  <td className="py-2">
                    <Link to={`/admin/quests/${q.id}`} className="font-medium hover:underline">
                      {q.title}
                    </Link>
                  </td>
                  <td className="text-ink-muted">{teamName(q.team_id)}</td>
                  <td className="text-xs">
                    <span className="rounded-full bg-bg-elevated px-2 py-0.5">{q.mode}</span>
                    {q.review_mode === 'review' ? <span className="ml-1 rounded-full bg-bg-elevated px-2 py-0.5">review</span> : null}
                  </td>
                  <td className="font-semibold">
                    {/*
                      M5.15: total XP across all splits. A
                      small "×N" chip when there are
                      multiple splits so the admin knows
                      the reward is split. Coins (when
                      >0) render as a separate "+N 🪙"
                      below the XP total in the coin-chip
                      colour, keeping the same vertical
                      rhythm as the player-facing cards.
                    */}
                    <div>{q.splits.reduce((s, x) => s + x.xp, 0)}</div>
                    {q.splits.length > 1 ? (
                      <div className="text-xs font-normal text-ink-dim">×{q.splits.length}</div>
                    ) : null}
                    {q.splits.reduce((s, x) => s + (x.coins ?? 0), 0) > 0 ? (
                      <div className="text-xs font-normal text-accent-warning">
                        +{q.splits.reduce((s, x) => s + (x.coins ?? 0), 0)} 🪙
                      </div>
                    ) : null}
                  </td>
                  <td className="text-ink-muted">{q.claim_count}{q.max_completions > 1 ? `/${q.max_completions}` : ''}</td>
                  <td>
                    {q.status === 'cancelled' ? <span className="text-accent-danger">{q.status}</span>
                      : q.status === 'archived' ? <span className="text-ink-dim">{q.status}</span>
                      : q.status === 'completed' ? <span className="text-accent-success">{q.status}</span>
                      : q.status === 'paused' ? <span className="text-accent-warning">{q.status}</span>
                      : <span>{q.status}</span>}
                  </td>
                  <td className="text-ink-muted">{q.due_at ? new Date(q.due_at).toLocaleDateString() : '—'}</td>
                  <td className="space-x-1 text-right">
                    <Link to={`/admin/quests/${q.id}`} className="btn">Open</Link>
                    {q.status === 'open' ? (
                      <>
                        <button
                          className="btn"
                          onClick={async () => {
                            if (!confirm(`Archive "${q.title}"? It will be hidden from default views but kept forever. You can restore it later.`)) return;
                            try { await archive.mutateAsync(q.id); }
                            catch (e) { alert(e instanceof ApiError ? e.message : 'Archive failed.'); }
                          }}
                        >
                          Archive
                        </button>
                        <button
                          className="btn-danger"
                          onClick={async () => {
                            if (!confirm(`Cancel "${q.title}"? In-flight claims stay; new claims blocked. You can restore it later.`)) return;
                            try { await cancel.mutateAsync(q.id); }
                            catch (e) { alert(e instanceof ApiError ? e.message : 'Cancel failed.'); }
                          }}
                        >
                          Cancel
                        </button>
                      </>
                    ) : null}
                    {q.status === 'cancelled' || q.status === 'archived' ? (
                      <button
                        className="btn"
                        onClick={async () => {
                          if (!confirm(`Restore "${q.title}" to open?`)) return;
                          try { await restore.mutateAsync(q.id); }
                          catch (e) { alert(e instanceof ApiError ? e.message : 'Restore failed.'); }
                        }}
                      >
                        Restore
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
